import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import ConfirmModal from "../modal/ConfirmModal";
// @ts-expect-error - JSX ファイルの型定義なし
import LanguageSwitcher from "../tab/LanguageSwitcher";
// @ts-expect-error - JSX ファイルの型定義なし
import ListBlock from "../block/ListBlock";
import IconButton from "../button/IconButton";
import ToolTip from "../tooltip/ToolTip";
import SvgLoadingAnimation from "../loading";
import StatusIndicator from "../status-indicator/StatusIndicator";
import Button from "../button/Button";

import CreditList from "./CreditList";
import CreditViewModal from "./CreditViewModal";
import EntryFormModal, {
  type EntryFormResult,
} from "./EntryFormModal";
import SectionFormModal, {
  type SectionKind,
  type SectionFormResult,
} from "./SectionFormModal";

import {
  useGetCreditsQuery,
  useCreateCreditMutation,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  useReorderCreditsMutation,
} from "../../app/services/credits";
import type { CreditItem, EntryPayload } from "../../app/services/credits";

// ===== styled =====

/** 全画面オーバーレイ。Preview / CreditViewModal と同じ構成（白背景・縦フレックス）。 */
const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background: #fff;
  z-index: 999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/** 左上の「戻る」ボタン領域。Preview の DesktopBackButton と同じ位置感。 */
const TopLeftBack = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
`;

/**
 * スクロール可能な本体エリア。固定フッターの上に確保される。
 * `padding-top: 56px` は左上の戻るボタン領域（top: 8px + IconButton 約 40px）と
 * コンテンツが重ならないように確保するスペース。
 */
const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 56px 16px 24px;
  display: flex;
  justify-content: center;
`;

/** 中央寄せの実コンテンツ幅。 */
const ContentInner = styled.div`
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ListWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/**
 * 下固定フッター。Careers の ModalFooter と同じレイアウト方針:
 *  - flex-shrink: 0 で常に下に固定
 *  - 上ボーダーで本体と区切り
 *  - 左右両端配置（左: 言語切替、右: クレジットを観るボタン）
 */
const FixedFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 24px;
  border-top: 1px solid var(--soremo-border);
  background: #fff;
`;

/**
 * フッター左の言語切り替え。LanguageSwitcher は `u-w100` で幅100%なため、
 * フッター内では幅を内容に合わせる。
 */
const FooterLanguage = styled.div`
  display: flex;
  align-items: center;

  & > div {
    width: auto;
  }
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ErrorBanner = styled.div`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--soremo-green);
  color: var(--soremo-green);
  border-radius: 4px;
  text-align: center;
`;

const WarningBanner = styled.div`
  width: 100%;
  padding: 8px 12px;
  border: 1px dashed var(--soremo-light-gray);
  color: var(--soremo-light-gray);
  border-radius: 4px;
  text-align: center;
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 24px 16px;
  text-align: center;
  color: var(--soremo-light-gray);
`;

// ===== helpers =====

const formatApiError = (err: unknown): string => {
  if (!err || typeof err !== "object") return "不明なエラーが発生しました。";
  const e = err as {
    status?: number | string;
    data?: { message?: string; errors?: Record<string, string[] | string> };
    error?: string;
    originalStatus?: number;
  };
  const status = e.status;
  const beMessage = e.data?.message;
  const fieldErrors = e.data?.errors;
  const fieldText = fieldErrors
    ? Object.entries(fieldErrors)
        .map(([k, v]) =>
          Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${v}`,
        )
        .join(" / ")
    : "";

  if (status === "FETCH_ERROR") {
    return "サーバーに接続できませんでした。ネットワーク／接続先 URL をご確認ください。";
  }
  // RTK Query は HTML 等 JSON 以外のレスポンスを `PARSING_ERROR` として扱う
  if (status === "PARSING_ERROR") {
    const httpStatus = e.originalStatus;
    const httpHint = httpStatus ? ` (HTTP ${httpStatus})` : "";
    return `サーバーから JSON 以外（HTML 等）が返されました${httpHint}。\nクレジット API のエンドポイントが BE 側にまだ実装/デプロイされていない可能性があります。\n（現状ルーティング先がフロントの index.html へフォールバックしていると思われます）`;
  }
  if (status === 404) {
    return "API エンドポイントが見つかりませんでした (404)。BE 側の実装/デプロイをご確認ください。";
  }
  if (status === 403) {
    return "操作権限がありません (403)。クレジットを編集できるのはプロデューサー / クライアント / 管理者のみです。";
  }
  if (status === 401) {
    return "認証が必要です (401)。再ログインしてください。";
  }
  if (status === 400) {
    return `入力内容にエラーがあります: ${beMessage || ""} ${fieldText}`.trim();
  }
  if (status === 500 || status === 502 || status === 503) {
    return `サーバーエラーが発生しました (${status})。`;
  }
  if (typeof status === "number") {
    return `エラーが発生しました (${status})${beMessage ? `: ${beMessage}` : ""}${fieldText ? ` / ${fieldText}` : ""}`;
  }
  return beMessage || e.error || "不明なエラーが発生しました。";
};

/**
 * EntryFormModal の初期値オブジェクトを CreditItem.entry から構築する。
 */
const buildEntryInitialValues = (
  item: CreditItem,
): Partial<EntryFormResult> | undefined => {
  const e = item.entry;
  if (!e) return undefined;
  return {
    role: e.role,
    role_en: e.role_en,
    display_name: e.display_name,
    display_name_en: e.display_name_en,
    link_url: e.link_url,
    linked_user_id: e.linked_user?.user_id ?? null,
  };
};

/**
 * SectionFormModal の初期値オブジェクトを CreditItem の section/subsection から構築する。
 */
const buildSectionInitialValues = (
  item: CreditItem,
  kind: SectionKind,
): Partial<SectionFormResult> => {
  if (kind === "section") {
    const s = item.section;
    return {
      title: s?.title ?? "",
      title_en: s?.title_en ?? "",
      note: s?.note ?? "",
      note_en: s?.note_en ?? "",
    };
  }
  const s = item.subsection;
  return {
    title: s?.title ?? "",
    title_en: s?.title_en ?? "",
  };
};

// ===== component =====

/** エントリー追加ボックスのラベル。EmptyState メッセージ内でも参照される。 */
const ADD_ENTRY_LABEL = "エントリーを追加";

type Status = "idle" | "saving" | "saved" | "error" | "deleting";

/**
 * エントリー作成・編集モーダルの状態。
 * - create: 新規作成
 * - edit: 既存項目を編集（item は必須）
 */
type EntryModalState =
  | { mode: "create" }
  | { mode: "edit"; item: CreditItem };

/**
 * セクション/サブセクション追加・編集モーダルの状態。
 * - create: 指定アイテムの上に挿入（insertAboveItemId）
 * - edit: 既存項目を編集（item は必須）
 */
type SectionModalState =
  | { kind: SectionKind; mode: "create"; insertAboveItemId: string | null }
  | { kind: SectionKind; mode: "edit"; item: CreditItem };

export interface CreditEditModalProps {
  projectId: string;
  /** クレジットビューア（CreditViewModal）に渡すプロジェクトタイトル。 */
  projectTitle?: string;
  /**
   * クレジットの編集権限（PRODUCER / ADMIN / CLIENT。
   * `memberPositionUtils.canEditCredit` で判定）を持っているか。
   * `false` の場合は閲覧のみ。
   */
  canEdit?: boolean;
  onClose: () => void;
}

const CreditEditModal: React.FC<CreditEditModalProps> = ({
  projectId,
  projectTitle,
  canEdit = true,
  onClose,
}) => {
  const { data, isLoading, error } = useGetCreditsQuery({ projectId });

  const [createCredit, { isLoading: isCreating }] = useCreateCreditMutation();
  const [updateCredit, { isLoading: isUpdating }] = useUpdateCreditMutation();
  const [deleteCredit, { isLoading: isDeleting }] = useDeleteCreditMutation();
  const [reorderCredits] = useReorderCreditsMutation();

  const [deleteTarget, setDeleteTarget] = useState<CreditItem | null>(null);

  const [language, setLanguage] = useState<"JP" | "EN">("JP");
  const [status, setStatus] = useState<Status>("idle");
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [entryModalState, setEntryModalState] = useState<EntryModalState | null>(null);
  const [entrySubmitError, setEntrySubmitError] = useState<string | null>(null);

  const [sectionModalState, setSectionModalState] = useState<SectionModalState | null>(null);
  const [sectionSubmitError, setSectionSubmitError] = useState<string | null>(null);

  const [creditViewOpen, setCreditViewOpen] = useState(false);

  const items = useMemo(
    () => (data?.data?.credits ?? []).slice().sort((a, b) => a.order - b.order),
    [data],
  );

  const showStatus = (next: Status) => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setStatus(next);
    if (next === "saved") {
      statusTimerRef.current = setTimeout(() => {
        setStatus("idle");
      }, 2000);
    }
  };

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    };
  }, []);

  // Esc で閉じる（Preview / CreditViewModal と同じ挙動）。
  // ただし内部モーダル（クレジットを観る・エントリー編集等）が開いている時は外側を閉じない。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (
        creditViewOpen ||
        entryModalState ||
        sectionModalState ||
        deleteTarget
      ) {
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, creditViewOpen, entryModalState, sectionModalState, deleteTarget]);

  // ----- handlers -----

  const handleEntrySubmit = async (result: EntryFormResult) => {
    if (!entryModalState) return;
    setEntrySubmitError(null);
    const payload: EntryPayload = {
      role: result.role,
      role_en: result.role_en,
      display_name: result.display_name,
      display_name_en: result.display_name_en,
      link_url: result.link_url,
      linked_user_id: result.linked_user_id ?? undefined,
    };
    showStatus("saving");
    try {
      if (entryModalState.mode === "create") {
        await createCredit({
          projectId,
          item_type: "entry",
          payload,
        }).unwrap();
      } else {
        await updateCredit({
          projectId,
          itemId: entryModalState.item.item_id,
          payload,
        }).unwrap();
      }
      showStatus("saved");
      setEntryModalState(null);
    } catch (e) {
      console.error("Failed to submit entry:", e);
      showStatus("error");
      setEntrySubmitError(formatApiError(e));
    }
  };

  const handleSectionSubmit = async (result: SectionFormResult) => {
    if (!sectionModalState) return;
    setSectionSubmitError(null);
    const payload =
      sectionModalState.kind === "section"
        ? {
            title: result.title,
            title_en: result.title_en,
            note: result.note ?? "",
            note_en: result.note_en ?? "",
          }
        : { title: result.title, title_en: result.title_en };

    showStatus("saving");
    try {
      if (sectionModalState.mode === "create") {
        const insertAbove = items.find(
          (it) => it.item_id === sectionModalState.insertAboveItemId,
        );
        await createCredit({
          projectId,
          item_type: sectionModalState.kind,
          order: insertAbove?.order,
          payload,
        }).unwrap();
      } else {
        await updateCredit({
          projectId,
          itemId: sectionModalState.item.item_id,
          payload,
        }).unwrap();
      }
      showStatus("saved");
      setSectionModalState(null);
    } catch (e) {
      console.error("Failed to submit section:", e);
      showStatus("error");
      setSectionSubmitError(formatApiError(e));
    }
  };

  const handleAddSectionAbove = (item: CreditItem) => {
    setSectionModalState({
      kind: "section",
      mode: "create",
      insertAboveItemId: item.item_id,
    });
  };

  const handleAddSubsectionAbove = (item: CreditItem) => {
    setSectionModalState({
      kind: "subsection",
      mode: "create",
      insertAboveItemId: item.item_id,
    });
  };

  const handleEdit = (item: CreditItem) => {
    if (item.item_type === "entry") {
      setEntryModalState({ mode: "edit", item });
    } else if (item.item_type === "section") {
      setSectionModalState({ kind: "section", mode: "edit", item });
    } else if (item.item_type === "subsection") {
      setSectionModalState({ kind: "subsection", mode: "edit", item });
    }
  };

  const handleDelete = (item: CreditItem) => {
    setDeleteTarget(item);
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    showStatus("deleting");
    try {
      await deleteCredit({
        projectId,
        itemId: deleteTarget.item_id,
      }).unwrap();
      showStatus("saved");
      setDeleteTarget(null);
    } catch (e) {
      console.error("Failed to delete credit:", e);
      showStatus("error");
    }
  };

  const handleReorder = async (orderedIds: string[]) => {
    showStatus("saving");
    try {
      await reorderCredits({ projectId, ordered_ids: orderedIds }).unwrap();
      showStatus("saved");
    } catch (e) {
      console.error("Failed to reorder credits:", e);
      showStatus("error");
    }
  };

  // ----- render -----

  // 403（権限不足）のみ致命的エラーとして全体ブロック。
  // 404 等の取得失敗は警告に留め、追加 UI は表示し続ける（既存情報の有無に関わらず作成可）。
  const errorStatus = (error as { status?: number } | undefined)?.status;
  const isForbidden = errorStatus === 403;
  const hasRecoverableFetchError = !!error && !isForbidden;

  const addEntryDisabled = isCreating || isUpdating;

  return createPortal(
    <FullscreenOverlay role="dialog" aria-modal="true">
      <TopLeftBack>
        <ToolTip description="戻る" placement="right">
          <IconButton onClick={onClose} icon="chevron_left" />
        </ToolTip>
      </TopLeftBack>

      <ScrollArea>
        <ContentInner>
          {isLoading ? (
            <div className="u-col-center u-w100" style={{ padding: 32 }}>
              <SvgLoadingAnimation />
            </div>
          ) : isForbidden ? (
            <ErrorBanner className="bodytext-13">
              クレジットを編集する権限がありません。
            </ErrorBanner>
          ) : (
            <ListWrapper>
              {hasRecoverableFetchError && (
                <WarningBanner className="bodytext-11">
                  既存のクレジットを読み込めませんでした。新しい項目の追加は可能です。
                </WarningBanner>
              )}
              {items.length === 0 ? (
                <EmptyState className="bodytext-13">
                  {hasRecoverableFetchError
                    ? `下の「${ADD_ENTRY_LABEL}」から最初の項目を追加してください。`
                    : `まだクレジットはありません。下の「${ADD_ENTRY_LABEL}」から追加してください。`}
                </EmptyState>
              ) : (
                <CreditList
                  items={items}
                  canEdit={canEdit}
                  language={language}
                  onReorder={handleReorder}
                  onAddSectionAbove={handleAddSectionAbove}
                  onAddSubsectionAbove={handleAddSubsectionAbove}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )}
              {/* エントリー追加ボックスは常にリストの一番下に配置（要件） */}
              {canEdit && (
                <ListBlock
                  type="add"
                  label={ADD_ENTRY_LABEL}
                  onClick={
                    addEntryDisabled
                      ? undefined
                      : () => setEntryModalState({ mode: "create" })
                  }
                />
              )}
            </ListWrapper>
          )}
        </ContentInner>
      </ScrollArea>

      <FixedFooter>
        <FooterLanguage>
          <LanguageSwitcher onLanguageChange={setLanguage} />
        </FooterLanguage>
        <FooterRight>
          {/* 編集画面の主アクション = primary filled（outlined は非推奨のため使わない）。 */}
          <Button
            size="small"
            theme="primary"
            variant="filled"
            leftIcon="movie"
            onClick={() => setCreditViewOpen(true)}
            disabled={isLoading || items.length === 0}
          >
            クレジットを観る
          </Button>
        </FooterRight>
      </FixedFooter>

      <StatusIndicator status={status} />

      {entryModalState && (
        <EntryFormModal
          projectId={projectId}
          mode={entryModalState.mode}
          initialValues={
            entryModalState.mode === "edit"
              ? buildEntryInitialValues(entryModalState.item)
              : undefined
          }
          isSubmitting={isCreating || isUpdating}
          submitError={entrySubmitError}
          onClose={() => {
            setEntryModalState(null);
            setEntrySubmitError(null);
          }}
          onSubmit={handleEntrySubmit}
        />
      )}

      {sectionModalState && (
        <SectionFormModal
          kind={sectionModalState.kind}
          mode={sectionModalState.mode}
          initialValues={
            sectionModalState.mode === "edit"
              ? buildSectionInitialValues(
                  sectionModalState.item,
                  sectionModalState.kind,
                )
              : undefined
          }
          isSubmitting={isCreating || isUpdating}
          submitError={sectionSubmitError}
          onClose={() => {
            setSectionModalState(null);
            setSectionSubmitError(null);
          }}
          onSubmit={handleSectionSubmit}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          message="このクレジット項目を削除しますか？"
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}

      {creditViewOpen && (
        <CreditViewModal
          projectId={projectId}
          projectTitle={projectTitle}
          onClose={() => setCreditViewOpen(false)}
        />
      )}
    </FullscreenOverlay>,
    document.body,
  );
};

export default CreditEditModal;
